import React, { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import api from "../api";

// Player Portal registrations only auto-link to the roster when the typed
// name matches exactly (see routes/player.js /register) — a spelling
// difference (Derek vs Derick, a missing middle name, etc.) leaves a real
// login sitting unconnected to that person's actual history. This screen
// shows the full picture — who's registered and linked, and who's
// registered but needs manual linking — instead of it needing a
// hand-edited database fix every time.
const NR_DEFAULT_SUBJECT = "⛳ Stay in the loop — register for the Player Portal";
const NR_DEFAULT_BODY =
`Hi {{first_name}},

You're on our roster but haven't yet set up your Player Portal account — it's where you can see your own results, verify scorecards and keep your details up to date.

It only takes a minute: golfsocietymanager.com/choose-login

Thanks,
Neil Workman`;
const NR_MERGE_TAGS = ["first_name", "name"];

export default function LinkAccounts() {
  const [linked, setLinked] = useState([]);
  const [unlinked, setUnlinked] = useState([]);
  const [notRegistered, setNotRegistered] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [toast, setToast] = useState("");

  // Separate "keep in touch" mail merge targeting NOT REGISTERED players --
  // additive only, does not touch the existing event/voting Mail Merge page
  // or its /mailmerge/send route at all.
  const [showMerge, setShowMerge] = useState(false);
  const [mergeSubject, setMergeSubject] = useState(NR_DEFAULT_SUBJECT);
  const [mergeBody, setMergeBody] = useState(NR_DEFAULT_BODY);
  const [mergeSelected, setMergeSelected] = useState({});
  const [mergeSending, setMergeSending] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const mergePanelRef = useRef(null);

  // Clicking "Mail Merge" can reveal the panel well below the fold once the
  // roster is large enough -- scroll it into view so opening it is never a
  // no-op from the organiser's point of view.
  useEffect(() => {
    if (showMerge && mergePanelRef.current) {
      mergePanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [showMerge]);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/players/portal-status"), api.get("/players")])
      .then(([statusRes, playerRes]) => {
        setLinked(statusRes.data.linked);
        setUnlinked(statusRes.data.unlinked);
        const nr = statusRes.data.notRegistered || [];
        setNotRegistered(nr);
        const initMerge = {};
        nr.forEach(p => { initMerge[p.id] = !!p.email; });
        setMergeSelected(initMerge);
        setPlayers(playerRes.data.filter(p => p.active));
      })
      .catch(() => setToast("Failed to load"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const linkAccount = async (accountId) => {
    const playerId = selected[accountId];
    if (!playerId) { showToast("Choose a player first"); return; }
    try {
      await api.put(`/players/link-account/${accountId}`, { player_id: parseInt(playerId) });
      showToast("Linked!");
      load();
    } catch(e) {
      showToast("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  // Same delete as the Dashboard's own Delete Player button (Players.jsx) --
  // a player with existing scores/results isn't actually erased, just
  // deactivated, so their history stays intact. Added here so an organiser
  // can clear out roster members they spot sitting unregistered without
  // having to go find them again on the Dashboard.
  const deletePlayer = async (id, name) => {
    if (!window.confirm(`Delete ${name}?`)) return;
    try {
      await api.delete("/players/" + id);
      showToast("Deleted");
      load();
    } catch(e) {
      showToast("Failed: " + (e.response?.data?.error || e.message));
    }
  };

  const mergeToggle = (id) => setMergeSelected(s => ({ ...s, [id]: !s[id] }));
  const mergeSelectAll = (val) => {
    const next = {};
    notRegistered.forEach(p => { next[p.id] = val && !!p.email; });
    setMergeSelected(next);
  };
  const mergeRecipientIds = notRegistered.filter(p => mergeSelected[p.id]).map(p => p.id);

  const sendMerge = async () => {
    if (!mergeRecipientIds.length) { showToast("Select at least one recipient"); return; }
    if (!window.confirm(`Send this email to ${mergeRecipientIds.length} player(s)? This cannot be undone.`)) return;
    setMergeSending(true);
    setMergeResult(null);
    try {
      const res = await api.post("/mailmerge/send-notregistered", { subject: mergeSubject, body: mergeBody, playerIds: mergeRecipientIds });
      setMergeResult(res.data);
    } catch(e) {
      setMergeResult({ error: e.response?.data?.error || "Network error" });
    }
    setMergeSending(false);
  };

  return (
    <Layout>
      <div className="flex flex-col h-full">
        <div className="bg-white border-b border-gray-300 px-4 py-3">
          <span className="font-semibold text-sm">Register Player Accounts</span>
          <span className="ml-3 text-xs text-gray-500">Who's registered for the Player Portal, and whether they're linked to your roster</span>
        </div>
        {toast && <div className="bg-green-50 text-green-700 text-sm px-4 py-2 border-b border-green-200">{toast}</div>}
        {loading && <div className="p-4 text-gray-500 text-sm">Loading...</div>}
        {!loading && (
          <div className="flex-1 overflow-auto p-4 flex flex-col gap-4">

            {/* Primary view: who's registered vs who isn't -- the question
                organisers actually come to this page to answer. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">

              <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-green-800 text-white text-xs font-bold px-4 py-2">REGISTERED ({linked.length})</div>
                <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100 bg-white sticky top-0">
                      <th className="px-4 py-1.5 font-medium">Name</th>
                      <th className="px-4 py-1.5 font-medium">Email</th>
                      <th className="px-4 py-1.5 font-medium">Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linked.map(a => (
                      <tr key={a.id} className="border-b border-gray-100 last:border-0">
                        <td className="px-4 py-2 font-medium">{a.player_name}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{a.email}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ""}</td>
                      </tr>
                    ))}
                    {linked.length === 0 && <tr><td colSpan={3} className="px-4 py-3 text-gray-400 text-sm">No one has registered yet.</td></tr>}
                  </tbody>
                </table>
                </div>
              </div>

              {/* On the roster, no Player Portal account at all yet. */}
              <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-500 text-white text-xs font-bold px-4 py-2 flex items-center justify-between">
                  <span>NOT REGISTERED ({notRegistered.length})</span>
                  <button
                    onClick={() => setShowMerge(s => !s)}
                    className="bg-white text-gray-700 hover:bg-gray-100 text-xs font-semibold px-2 py-1 rounded"
                  >
                    ✉️ Mail Merge
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 border-b border-gray-100 bg-white sticky top-0">
                      <th className="px-4 py-1.5 font-medium">Name</th>
                      <th className="px-4 py-1.5 font-medium">Email</th>
                      <th className="px-4 py-1.5 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {notRegistered.map(p => (
                      <tr key={p.id} className="border-b border-gray-100 last:border-0">
                        <td className="px-4 py-2 font-medium">{p.name}</td>
                        <td className="px-4 py-2 text-xs text-gray-500">{p.email}</td>
                        <td className="px-4 py-2 text-right">
                          <button
                            onClick={() => deletePlayer(p.id, p.name)}
                            className="bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-2 py-1 rounded"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {notRegistered.length === 0 && <tr><td colSpan={3} className="px-4 py-3 text-gray-400 text-sm">Everyone on the roster has registered.</td></tr>}
                  </tbody>
                </table>
                </div>
              </div>

            </div>

            {/* "Keep in touch" mail merge for NOT REGISTERED players --
                additive, separate from the existing event/voting Mail Merge
                page. Only shown when toggled open. */}
            {showMerge && (
              <div ref={mergePanelRef} className="bg-white border border-gray-300 rounded-lg overflow-hidden">
                <div className="bg-gray-700 text-white text-xs font-bold px-4 py-2">MAIL MERGE — NOT REGISTERED PLAYERS</div>
                <div className="p-4 flex flex-col md:flex-row gap-4">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs font-bold text-gray-600 mt-0 mb-1">Subject</label>
                    <input value={mergeSubject} onChange={e => setMergeSubject(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm box-border" />
                    <label className="block text-xs font-bold text-gray-600 mt-3 mb-1">Body</label>
                    <textarea value={mergeBody} onChange={e => setMergeBody(e.target.value)} rows={8}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm box-border font-sans" />
                    <div className="text-xs text-gray-500 mt-1.5">
                      Merge tags: {NR_MERGE_TAGS.map(t => <code key={t} className="bg-indigo-50 text-indigo-800 rounded px-1.5 py-0.5 mr-1.5 text-xs">{`{{${t}}}`}</code>)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-gray-600">Recipients ({notRegistered.length}) · {mergeRecipientIds.length} selected</span>
                      <div>
                        <button onClick={() => mergeSelectAll(true)} className="text-xs border border-gray-300 bg-white rounded px-2 py-1 ml-1">All</button>
                        <button onClick={() => mergeSelectAll(false)} className="text-xs border border-gray-300 bg-white rounded px-2 py-1 ml-1">None</button>
                      </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                      {notRegistered.map(p => {
                        const hasEmail = !!p.email;
                        return (
                          <label key={p.id} className={`flex items-center gap-2 px-2.5 py-2 border-b border-gray-100 last:border-0 text-sm ${hasEmail ? "" : "opacity-60"}`}>
                            <input type="checkbox" checked={!!mergeSelected[p.id]} disabled={!hasEmail} onChange={() => mergeToggle(p.id)} />
                            <span className="flex-1">{p.name}</span>
                            <span className={`text-xs ${hasEmail ? "text-gray-400" : "font-bold text-amber-700"}`}>{hasEmail ? "" : "no email"}</span>
                          </label>
                        );
                      })}
                      {notRegistered.length === 0 && <div className="px-3 py-3 text-gray-400 text-sm">Nobody to email.</div>}
                    </div>
                    <button onClick={sendMerge} disabled={mergeSending || !mergeRecipientIds.length}
                      className="mt-3 w-full bg-green-800 hover:bg-green-900 disabled:opacity-60 text-white font-bold rounded-lg py-2.5 text-sm">
                      {mergeSending ? "Sending..." : `Send to ${mergeRecipientIds.length} player(s)`}
                    </button>
                    {mergeResult && (
                      <div className="mt-2 text-xs">
                        {mergeResult.error && <div className="text-red-700">{mergeResult.error}</div>}
                        {mergeResult.sent && <div className="text-green-700">Sent: {mergeResult.sent.length}</div>}
                        {mergeResult.skipped && mergeResult.skipped.length > 0 && <div className="text-red-700">Skipped: {mergeResult.skipped.join(", ")}</div>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Secondary: name-mismatch cleanup. Demoted below the main view
                since it's usually empty -- most registrations auto-link. */}
            <div className="bg-white border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-amber-600 text-white text-xs font-bold px-4 py-2">NEEDS LINKING ({unlinked.length}) — registered, but the name didn't match the roster exactly</div>
              <table className="w-full text-sm">
                <tbody>
                  {unlinked.map(a => (
                    <tr key={a.id} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-2">
                        <div className="font-medium">{a.first_name} {a.last_name}</div>
                        <div className="text-xs text-gray-500">{a.email}</div>
                      </td>
                      <td className="px-4 py-2">
                        <select
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                          value={selected[a.id] || ""}
                          onChange={e => setSelected(prev => ({ ...prev, [a.id]: e.target.value }))}
                        >
                          <option value="">— Select player —</option>
                          {players.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => linkAccount(a.id)}
                          className="bg-green-700 hover:bg-green-800 text-white text-xs font-semibold px-3 py-1.5 rounded"
                        >
                          Link
                        </button>
                      </td>
                    </tr>
                  ))}
                  {unlinked.length === 0 && <tr><td className="px-4 py-3 text-gray-400 text-sm">Nothing to link.</td></tr>}
                </tbody>
              </table>
            </div>

          </div>
        )}
      </div>
    </Layout>
  );
}
